/**
 * LivePlanTrackerPanel Component
 * 
 * Displays the live tracking of a selected race plan. Shows:
 *   - Current status (on track / ahead / behind / critical)
 *   - Stint-by-stint comparison of planned vs actual
 *   - Re-planned remaining stints based on real data
 *   - Key deltas (pace, fuel, laps)
 */

import React, { useMemo, useRef, useEffect, useState } from "react";
import type { RacePlan, StaticStrategyInput } from "../utils/staticStrategyPlanner";
import { LivePlanTracker, LivePlanStatus, TrackedStint } from "../utils/livePlanTracker";

interface LivePlanTrackerPanelProps {
  selectedPlan: RacePlan;
  planInput: StaticStrategyInput;
  // Live telemetry data
  currentLap: number;  // eslint-disable-line @typescript-eslint/no-unused-vars
  currentFuel: number | null;
  avgLapTime: number | null;
  avgFuelPerLap: number | null;
  laps: { lapNumber: number; lapTime: number | null; fuelLevel: number | null }[];
  raceDurationMinutes: number;  // eslint-disable-line @typescript-eslint/no-unused-vars
  // Callback to clear the plan
  onClearPlan?: () => void;
}

const STATUS_STYLES: Record<string, { bg: string; border: string; text: string; icon: string }> = {
  on_track: { bg: 'bg-green-900/20', border: 'border-green-500/40', text: 'text-green-400', icon: '✓' },
  ahead:    { bg: 'bg-blue-900/20',  border: 'border-blue-500/40',  text: 'text-blue-400',  icon: '▲' },
  behind:   { bg: 'bg-yellow-900/20', border: 'border-yellow-500/40', text: 'text-yellow-400', icon: '▼' },
  critical: { bg: 'bg-red-900/20',   border: 'border-red-500/40',   text: 'text-red-400',   icon: '⚠' },
};

function formatTime(seconds: number): string {
  const h = Math.floor(Math.abs(seconds) / 3600);
  const m = Math.floor((Math.abs(seconds) % 3600) / 60);
  const s = Math.floor(Math.abs(seconds) % 60);
  const sign = seconds < 0 ? '-' : '';
  if (h > 0) return `${sign}${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${sign}${m}:${s.toString().padStart(2, '0')}`;
}

function formatLapTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = (seconds % 60).toFixed(2);
  return `${m}:${parseFloat(s) < 10 ? '0' : ''}${s}`;
}

function formatDelta(value: number, unit: string, invert = false): JSX.Element {
  const isGood = invert ? value > 0 : value < 0;
  const isBad = invert ? value < 0 : value > 0;
  const color = isGood ? 'text-green-400' : isBad ? 'text-red-400' : 'text-slate-400';
  const sign = value > 0 ? '+' : '';
  return <span className={color}>{sign}{value.toFixed(2)}{unit}</span>;
}

export const LivePlanTrackerPanel: React.FC<LivePlanTrackerPanelProps> = ({
  selectedPlan,
  planInput,
  currentLap: _currentLap,
  currentFuel,
  avgLapTime,
  avgFuelPerLap,
  laps,
  raceDurationMinutes: _raceDurationMinutes,
  onClearPlan,
}) => {
  // Reserved for future use (progress bar, time-based display)
  void _currentLap;
  void _raceDurationMinutes;
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Create tracker instance, stable across renders for the same plan
  const trackerRef = useRef<LivePlanTracker | null>(null);
  const lastPlanIdRef = useRef<string | null>(null);

  if (lastPlanIdRef.current !== selectedPlan.id) {
    trackerRef.current = new LivePlanTracker(selectedPlan, planInput);
    lastPlanIdRef.current = selectedPlan.id;
  }

  const tracker = trackerRef.current!;

  // Feed lap data into the tracker
  useEffect(() => {
    for (let i = 1; i < laps.length; i++) {
      const prev = laps[i - 1];
      const curr = laps[i];
      if (curr.lapTime && curr.lapTime > 0 && prev.fuelLevel !== null && curr.fuelLevel !== null) {
        const fuelUsed = Math.max(0, prev.fuelLevel - curr.fuelLevel);
        // If fuel went up, it means a pit stop happened — fuelUsed would be negative, 
        // so we skip this as a "driving" data point but the tracker auto-detects pits
        tracker.recordLap({
          lapNumber: curr.lapNumber,
          lapTime: curr.lapTime,
          fuelUsed: fuelUsed > 0 ? fuelUsed : 0,
          fuelRemaining: curr.fuelLevel,
        });
      }
    }
  }, [laps, tracker]);

  // Calculate elapsed race time (sum of all lap times + estimated pit times)
  const elapsedTime = useMemo(() => {
    let total = 0;
    for (const lap of laps) {
      if (lap.lapTime && lap.lapTime > 0) total += lap.lapTime;
    }
    return total;
  }, [laps]);

  // Get status update
  const status: LivePlanStatus | null = useMemo(() => {
    if (laps.length < 2 || currentFuel === null) return null;
    return tracker.update(currentFuel, elapsedTime);
  }, [tracker, laps.length, currentFuel, elapsedTime]);

  if (!status) {
    return (
      <div className="bg-slate-800 p-6 rounded-xl border border-blue-500/40 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="text-blue-400 text-lg font-bold">📋 Live Plan Tracker</span>
            <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/40">
              {selectedPlan.name}
            </span>
          </div>
          {onClearPlan && (
            <button onClick={onClearPlan} className="text-xs text-slate-500 hover:text-red-400 transition-colors">
              ✕ Clear Plan
            </button>
          )}
        </div>
        <p className="text-slate-400 text-sm">Waiting for lap data... Complete at least 2 laps to start tracking.</p>
      </div>
    );
  }

  const statusStyle = STATUS_STYLES[status.status] || STATUS_STYLES.on_track;

  return (
    <div className={`bg-slate-800 p-6 rounded-xl border ${statusStyle.border} mb-6`}>
      {/* Header */}
      <div 
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-2">
          <span className="text-blue-400 text-lg font-bold">📋 Live Plan Tracker</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${statusStyle.bg} ${statusStyle.text} ${statusStyle.border}`}>
            {statusStyle.icon} {status.status.replace('_', ' ').toUpperCase()}
          </span>
        </div>
        <div className="flex items-center gap-3">
          {onClearPlan && (
            <button 
              onClick={(e) => { e.stopPropagation(); onClearPlan(); }} 
              className="text-xs text-slate-500 hover:text-red-400 transition-colors"
            >
              ✕ Clear
            </button>
          )}
          <span className={`text-slate-400 transition-transform duration-200 text-sm ${isCollapsed ? '' : 'rotate-180'}`}>▼</span>
        </div>
      </div>

      {!isCollapsed && <>
        {/* Status Message */}
        <div className={`mt-3 p-3 rounded-lg ${statusStyle.bg} border ${statusStyle.border}`}>
          <div className={`text-sm font-medium ${statusStyle.text}`}>{status.statusMessage}</div>
          <div className="text-xs text-slate-400 mt-1">
            Plan: {selectedPlan.name} • Stint {status.currentStint}/{selectedPlan.stints.length}
          </div>
        </div>

        {/* Key Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
          <MetricCard
            label="Current Lap"
            value={`${status.currentLap}`}
            subtext={`of ~${status.projectedTotalLaps} projected`}
          />
          <MetricCard
            label="Fuel"
            value={`${status.currentFuel.toFixed(1)}L`}
            subtext={avgFuelPerLap ? `${(status.currentFuel / avgFuelPerLap).toFixed(1)} laps left` : ''}
          />
          <MetricCard
            label="Remaining Stops"
            value={`${status.projectedRemainingStops}`}
            subtext={`plan had ${selectedPlan.pitStops}`}
          />
          <MetricCard
            label="Lap Delta"
            value={status.lapsDelta >= 0 ? `+${status.lapsDelta}` : `${status.lapsDelta}`}
            subtext="vs plan"
            valueColor={status.lapsDelta > 0 ? 'text-green-400' : status.lapsDelta < 0 ? 'text-red-400' : 'text-white'}
          />
        </div>

        {/* Pace Comparison */}
        <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div className="bg-slate-700/50 rounded-lg p-3">
            <div className="text-slate-400 text-xs mb-1">Lap Time</div>
            <div className="flex items-baseline gap-2">
              <span className="text-white font-mono font-bold">
                {avgLapTime ? formatLapTime(avgLapTime) : '--'}
              </span>
              {avgLapTime && (
                <span className="text-xs">
                  {formatDelta(avgLapTime - selectedPlan.adjustedLapTime, 's')}
                  <span className="text-slate-500 ml-1">vs {formatLapTime(selectedPlan.adjustedLapTime)}</span>
                </span>
              )}
            </div>
          </div>
          <div className="bg-slate-700/50 rounded-lg p-3">
            <div className="text-slate-400 text-xs mb-1">Fuel/Lap</div>
            <div className="flex items-baseline gap-2">
              <span className="text-white font-mono font-bold">
                {avgFuelPerLap ? `${avgFuelPerLap.toFixed(2)}L` : '--'}
              </span>
              {avgFuelPerLap && (
                <span className="text-xs">
                  {formatDelta(avgFuelPerLap - selectedPlan.adjustedFuelPerLap, 'L')}
                  <span className="text-slate-500 ml-1">vs {selectedPlan.adjustedFuelPerLap.toFixed(2)}L</span>
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Stint Tracking Table */}
        <div className="mt-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Stint Progress</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-slate-500 border-b border-slate-700/50">
                  <th className="px-2 py-1 text-left">Stint</th>
                  <th className="px-2 py-1 text-center">Status</th>
                  <th className="px-2 py-1 text-right">Laps</th>
                  <th className="px-2 py-1 text-right">Avg Lap</th>
                  <th className="px-2 py-1 text-right">Fuel/Lap</th>
                  <th className="px-2 py-1 text-right">Fuel Start</th>
                  <th className="px-2 py-1 text-right">Fuel End</th>
                  <th className="px-2 py-1 text-center">Pit</th>
                </tr>
              </thead>
              <tbody>
                {status.stints.map((stint) => (
                  <StintTrackingRow key={stint.stintNumber} stint={stint} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Updated Plan (from current point) */}
        {status.updatedPlan && status.updatedPlan.stints.length > 0 && (
          <div className="mt-4">
            <h3 className="text-sm font-semibold text-blue-300 mb-2">
              📊 Re-planned from Current State ({status.updatedPlan.totalLaps} laps remaining, {status.updatedPlan.pitStops} stops)
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-500 border-b border-slate-700/50">
                    <th className="px-2 py-1 text-left">Stint</th>
                    <th className="px-2 py-1 text-right">Laps</th>
                    <th className="px-2 py-1 text-right">Fuel Start</th>
                    <th className="px-2 py-1 text-right">Fuel End</th>
                    <th className="px-2 py-1 text-right">Stint Time</th>
                    <th className="px-2 py-1 text-center">Pit</th>
                    <th className="px-2 py-1 text-right">Fuel Added</th>
                  </tr>
                </thead>
                <tbody>
                  {status.updatedPlan.stints.map((stint) => (
                    <tr key={stint.stintNumber} className="border-b border-slate-800/50">
                      <td className="px-2 py-1 font-mono text-slate-300">R{stint.stintNumber}</td>
                      <td className="px-2 py-1 text-right font-mono text-white">{stint.laps}</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300">{stint.fuelAtStart.toFixed(1)}L</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300">{stint.fuelAtEnd.toFixed(1)}L</td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300">{formatTime(stint.stintTime)}</td>
                      <td className="px-2 py-1 text-center">
                        {stint.pitAfter ? (
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            stint.pitType === 'full'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-orange-500/20 text-orange-400'
                          }`}>
                            {stint.pitType === 'full' ? 'Full' : 'Splash'}
                          </span>
                        ) : <span className="text-slate-600">—</span>}
                      </td>
                      <td className="px-2 py-1 text-right font-mono text-slate-300">
                        {stint.fuelAdded > 0 ? `+${stint.fuelAdded.toFixed(1)}L` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>}
    </div>
  );
};

/**
 * Small metric card sub-component
 */
const MetricCard: React.FC<{
  label: string;
  value: string;
  subtext?: string;
  valueColor?: string;
}> = ({ label, value, subtext, valueColor = 'text-white' }) => (
  <div className="bg-slate-700/50 rounded-lg p-3 text-center">
    <div className="text-slate-400 text-xs">{label}</div>
    <div className={`text-lg font-bold font-mono ${valueColor}`}>{value}</div>
    {subtext && <div className="text-xs text-slate-500">{subtext}</div>}
  </div>
);

/**
 * Stint tracking row — shows planned vs actual for each stint
 */
const StintTrackingRow: React.FC<{ stint: TrackedStint }> = ({ stint }) => {
  const statusColors = {
    completed: 'text-slate-300',
    active: 'text-blue-400',
    planned: 'text-slate-500',
  };

  const statusBadge = {
    completed: 'bg-green-500/20 text-green-400',
    active: 'bg-blue-500/20 text-blue-400',
    planned: 'bg-slate-700 text-slate-500',
  };

  return (
    <tr className={`border-b border-slate-800/50 ${stint.status === 'active' ? 'bg-blue-900/10' : ''}`}>
      <td className={`px-2 py-1 font-mono ${statusColors[stint.status]}`}>
        S{stint.stintNumber}
      </td>
      <td className="px-2 py-1 text-center">
        <span className={`px-1.5 py-0.5 rounded text-xs ${statusBadge[stint.status]}`}>
          {stint.status === 'completed' ? '✓' : stint.status === 'active' ? '●' : '○'}
        </span>
      </td>
      <td className="px-2 py-1 text-right font-mono">
        <span className={statusColors[stint.status]}>
          {stint.status !== 'planned' ? stint.actualLaps : stint.plannedLaps}
        </span>
        <span className="text-slate-600">/{stint.plannedLaps}</span>
      </td>
      <td className="px-2 py-1 text-right font-mono">
        {stint.actualAvgLapTime !== null ? (
          <span>
            <span className={statusColors[stint.status]}>
              {formatLapTime(stint.actualAvgLapTime)}
            </span>
            {stint.lapTimeDelta !== null && (
              <span className="ml-1">
                {formatDelta(stint.lapTimeDelta, '')}
              </span>
            )}
          </span>
        ) : (
          <span className="text-slate-600">{formatLapTime(stint.plannedLapTime)}</span>
        )}
      </td>
      <td className="px-2 py-1 text-right font-mono">
        {stint.actualAvgFuelPerLap !== null ? (
          <span>
            <span className={statusColors[stint.status]}>
              {stint.actualAvgFuelPerLap.toFixed(2)}
            </span>
            {stint.fuelDelta !== null && (
              <span className="ml-1">
                {formatDelta(stint.fuelDelta, '')}
              </span>
            )}
          </span>
        ) : (
          <span className="text-slate-600">{stint.plannedFuelPerLap.toFixed(2)}</span>
        )}
      </td>
      <td className="px-2 py-1 text-right font-mono">
        {stint.actualFuelAtStart !== null ? (
          <span className={statusColors[stint.status]}>{stint.actualFuelAtStart.toFixed(1)}</span>
        ) : (
          <span className="text-slate-600">{stint.plannedFuelAtStart.toFixed(1)}</span>
        )}
      </td>
      <td className="px-2 py-1 text-right font-mono">
        {stint.actualFuelAtEnd !== null ? (
          <span className={statusColors[stint.status]}>{stint.actualFuelAtEnd.toFixed(1)}</span>
        ) : (
          <span className="text-slate-600">—</span>
        )}
      </td>
      <td className="px-2 py-1 text-center">
        {stint.plannedPitAfter ? (
          <span className={`px-1.5 py-0.5 rounded text-xs ${
            stint.plannedPitType === 'full'
              ? 'bg-green-500/20 text-green-400'
              : 'bg-orange-500/20 text-orange-400'
          }`}>
            {stint.plannedPitType === 'full' ? 'Full' : 'Splash'}
          </span>
        ) : <span className="text-slate-600">—</span>}
      </td>
    </tr>
  );
};
